import { ChevronIcon } from '@p2p/assets/svg';
import { Button, Typography } from '@p2p/components';
import { Timer } from '@p2p/entities/payment/Timer/Timer';
import { isNoQrBank } from '@p2p/entities/qrInvoice/noQrBanks';
import { hasProviderQrImage } from '@p2p/entities/qrInvoice/providerQrImage';
import { isSberUzbekistan } from '@p2p/entities/qrInvoice/sber';
import { PaymentCancelModal, usePaymentTemplate } from '@p2p/entities/templates';
import { ModalBankTranfer } from '@p2p/pages/Payment/components/TransferForm/ModalBankTranfer/ModalBankTranfer';
import { ModalIncrementApplied } from '@p2p/pages/Payment/components/TransferForm/ModalIncrementApplied/ModalIncrementApplied';
import { TransferUpload } from '@p2p/pages/Payment/components/TransferForm/TransferUpload';
import { TGetOrderResponse, TPageSettingsResponse } from '@p2p/services';
import { getPaymentAmount, getProviderPaymentCurrency } from '@p2p/shared/functions';
import { useDeviceDetect } from '@p2p/shared/hooks';
import { routes } from '@p2p/shared/routing';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AccountNumberInfoItems,
  InstructionInvoice,
  OpenBankAppButton,
  QrCodeInvoice,
  QRReturnModal,
  SberQrInvoiceWarningModal,
} from './components';
import {
  generateQRCodeValue,
  generateRandomId,
} from './components/QRCodeInvoice/QRCodeInvoice.utils';
import styles from './TransferInvoice.module.scss';

type TransferInvoiceProps = {
  order: TGetOrderResponse;
  paramId: string;
  pageSettings: TPageSettingsResponse;
};

export const TransferInvoice = ({ paramId, pageSettings, order }: TransferInvoiceProps) => {
  const navigate = useNavigate();
  const { isMobile } = useDeviceDetect();
  const { setActiveIndex } = usePaymentTemplate();
  const [isShowCancelModal, setIsShowCancelModal] = useState(false);
  // Для банков без QR (Babilon-M) реквизиты — единственный способ оплаты,
  // поэтому блок открыт сразу, а не по клику.
  const [isDropdownOpen, setIsDropdownOpen] = useState(() => isNoQrBank(order));
  const [isShowUpload, setIsShowUpload] = useState(false);
  const [isModalIncrementAppliedOpen, setIsModalIncrementAppliedOpen] = useState(true);
  const [qrRandomId] = useState(() => generateRandomId());
  // P2P-3043: QR-картинка провайдера — реквизита нет (заглушка), блок реквизитов не показываем.
  const isProviderQrImageOrder = hasProviderQrImage(order);
  // P2P-2851 (03.11.2026): QR MeridianPay оплачивают в рупиях, а заявка рублёвая. Сумму в
  // валюте провайдера (qr_amount) плательщик вводит в банке сам: в QR её нет. Показываем
  // только её, рублёвую сумму заявки у таких заявок не выводим (просьба PayGo).
  const providerAmount = getProviderPaymentCurrency(order) ? getPaymentAmount(order) : null;

  useEffect(() => {
    setActiveIndex(2);
  }, [setActiveIndex]);

  const handlePaid = () => {
    const transferUploadFiles = JSON.parse(localStorage.getItem('transfer_upload_files') || '{}');
    const files = transferUploadFiles.orderId !== paramId ? 0 : transferUploadFiles?.files;

    if (transferUploadFiles.orderId !== paramId) {
      localStorage.removeItem('transfer_upload_files');
    }

    if (pageSettings.receipt && files < 3) {
      setIsShowUpload(true);
    } else {
      navigate(routes.transferSearch.path({ id: paramId }));
    }
  };

  if (isShowUpload) {
    return <TransferUpload order={order} handleBack={() => setIsShowUpload(false)} />;
  }

  return (
    <>
      <div className={styles.invoiceWrapper}>
        <div className={styles.wrapper}>
          <div className={styles.head}>
            <div className={styles.headTitle}>
              <Typography variant="h1" color="heading">
                {isNoQrBank(order) && !order.invoice_url && !isProviderQrImageOrder
                  ? 'Оплата по номеру телефона'
                  : 'Оплата по QR-коду'}
              </Typography>
              <Timer time={order.time_left} className={styles.headTimer} />
            </div>
            <div className={styles.headSubtitle}>
              <Typography variant="main-sum" color="heading">
                {providerAmount ? providerAmount.amount : order.expected_income}
              </Typography>
              <Typography variant="h1" color="heading">
                {providerAmount ? providerAmount.currency : 'RUB'}
              </Typography>
            </div>
          </div>
          <QrCodeInvoice order={order} randomId={qrRandomId} />
          <OpenBankAppButton order={order} />
          <InstructionInvoice order={order} />
          <div className={styles.actions}>
            <Button onClick={handlePaid}>Я оплатил</Button>
            <Button onClick={() => setIsShowCancelModal(true)} variant="outlined">
              Отменить платёж
            </Button>
          </div>
        </div>
        {/* P2P-2511: для SimplePay QR (приходит готовый emvco) показываем только
            QR — блок «Оплата по номеру счёта» скрываем.
            P2P-2846: то же для payment_via_link (YouTrade Альфа QR и аналоги) --
            реквизит технический (номер счёта/телефона -- заглушка), показывать
            плательщику нечего и нельзя (жалоба интеграционного партнёра,
            17.09.2026: "чтобы игрок не мог чекнуть реки").
            P2P-3043: то же для QR-картинки провайдера (Viatrum PAYMENT_LINK). */}
        {!order.emvco && !order.payment_via_link && !isProviderQrImageOrder && (
          <div className={styles.wrapper}>
            <div
              className={styles.accountNumberHead}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <Typography variant="h1" color="heading" component="h2">
                {isSberUzbekistan(order) || isNoQrBank(order) || order.invoice_url
                  ? 'Оплата по номеру телефона'
                  : 'Оплата по номеру счёта'}
              </Typography>
              <ChevronIcon
                className={classNames(styles.accountNumberIcon, {
                  [styles.accountNumberIconOpen]: isDropdownOpen,
                })}
              />
            </div>
            {isDropdownOpen && <AccountNumberInfoItems order={order} randomId={qrRandomId} />}
          </div>
        )}
      </div>
      <PaymentCancelModal
        orderStatus={order.status}
        isActive={isShowCancelModal}
        setIsActive={setIsShowCancelModal}
        pageSettings={pageSettings}
        orderId={order.id}
      />
      <SberQrInvoiceWarningModal order={order} />
      {isMobile && <QRReturnModal onSuccess={handlePaid} onClose={() => setIsDropdownOpen(true)} />}
      {pageSettings.notification &&
        !isShowCancelModal &&
        !(isModalIncrementAppliedOpen && order.increment_applied) && (
          <ModalBankTranfer order={order} />
        )}
      {order.id && order.increment_applied && (
        <ModalIncrementApplied
          uid={order.id}
          sumText={`${order.expected_income.toLocaleString('ru-RU')} ${order?.currency_code_to}`}
          isCancelModalOpen={isShowCancelModal}
          showCancelModal={() => setIsShowCancelModal(true)}
          isOpen={isModalIncrementAppliedOpen}
          setIsOpen={setIsModalIncrementAppliedOpen}
        />
      )}
    </>
  );
};
